import type { MCPAction } from '@/types/tool'
import type { Tag } from '@/services/TagService'
import type { AgentDocument } from '@/types/agent'

export type MentionCategory = 'system' | 'tool' | 'tag' | 'document' | 'variable'

export type MentionSuggestion = {
  token: string
  label: string
  category: MentionCategory
  description?: string
  badgeEmoji?: string
  badgeColor?: string
}

export type MentionContext = {
  start: number
  end: number
  query: string
}

type SystemMentionSuggestion = {
  token: string
  label: string
  canonical: string
  aliasOf?: string
}

export const DYNAMIC_TOKEN_REGEX = /\{\{([a-zA-Z][a-zA-Z0-9_.]*)\}\}/g
export const AT_ESCAPE_SENTINEL = '\u0000AT_ESCAPE\u0000'
export const TOKEN_BOUNDARY_CHARS = '()[]{}<"\'`>.,;:!?'

export const SYSTEM_ALIAS_TO_CANONICAL: Record<string, string> = {
  name: 'system.name',
  phone: 'system.phone',
  email: 'system.email',
}

export const KNOWN_SYSTEM_TOKENS = new Set([
  'system.name',
  'system.phone',
  'system.email',
  'system.user_id',
  'system.conversation_id',
  'system.source',
  'system.channel_user_id',
  'system.channel_conversation_id',
  'system.conversation_title',
  'system.conversation_instructions',
  'system.contact_id',
  'system.session_id',
  'system.external_session_id',
  'system.avatar_id',
  'system.request_id',
  'system.current_route',
  'system.metadata_json',
])

const SYSTEM_MENTION_SUGGESTIONS: SystemMentionSuggestion[] = [
  { token: 'name', label: 'name', canonical: 'system.name', aliasOf: 'system.name' },
  { token: 'phone', label: 'phone', canonical: 'system.phone', aliasOf: 'system.phone' },
  { token: 'email', label: 'email', canonical: 'system.email', aliasOf: 'system.email' },
  { token: 'system.user_id', label: 'system.user_id', canonical: 'system.user_id' },
  { token: 'system.conversation_id', label: 'system.conversation_id', canonical: 'system.conversation_id' },
  { token: 'system.source', label: 'system.source', canonical: 'system.source' },
  { token: 'system.channel_user_id', label: 'system.channel_user_id', canonical: 'system.channel_user_id' },
  { token: 'system.channel_conversation_id', label: 'system.channel_conversation_id', canonical: 'system.channel_conversation_id' },
  { token: 'system.conversation_title', label: 'system.conversation_title', canonical: 'system.conversation_title' },
  { token: 'system.conversation_instructions', label: 'system.conversation_instructions', canonical: 'system.conversation_instructions' },
  { token: 'system.contact_id', label: 'system.contact_id', canonical: 'system.contact_id' },
  { token: 'system.session_id', label: 'system.session_id', canonical: 'system.session_id' },
  { token: 'system.external_session_id', label: 'system.external_session_id', canonical: 'system.external_session_id' },
  { token: 'system.avatar_id', label: 'system.avatar_id', canonical: 'system.avatar_id' },
  { token: 'system.request_id', label: 'system.request_id', canonical: 'system.request_id' },
  { token: 'system.current_route', label: 'system.current_route', canonical: 'system.current_route' },
  { token: 'system.metadata_json', label: 'system.metadata_json', canonical: 'system.metadata_json' },
]

export function uniqueList(items: string[]): string[] {
  return Array.from(new Set(items))
}

export function hasDynamicTokenBoundary(text: string, atIndex: number): boolean {
  if (atIndex <= 0) return true
  const previousChar = text[atIndex - 1]
  return /\s/.test(previousChar) || TOKEN_BOUNDARY_CHARS.includes(previousChar)
}

export function extractMentionedDynamicTokens(text: string): string[] {
  if (!text.trim()) return []
  const tokens: string[] = []
  for (const match of text.matchAll(DYNAMIC_TOKEN_REGEX)) {
    const token = String(match[1] || '').trim().toLowerCase()
    if (!token) continue
    tokens.push(SYSTEM_ALIAS_TO_CANONICAL[token] || token)
  }
  return uniqueList(tokens)
}

export function extractDynamicVariableKeysFromTool(tool: MCPAction): string[] {
  const httpConfig = tool.http_config
  if (!httpConfig) return []

  const schemas = [
    httpConfig.path_params_schema,
    httpConfig.query_params_schema,
    httpConfig.request_body_schema,
  ]

  const keys: string[] = []
  for (const schema of schemas) {
    if (!Array.isArray(schema)) continue
    for (const item of schema) {
      if (!item || typeof item !== 'object') continue
      const itemRecord = item as unknown as Record<string, unknown>
      const valueType = String(itemRecord.value_type || '')
      if (valueType !== 'dynamic_variable') continue

      const rawKey =
        String(itemRecord.variable_key || '').trim() ||
        String(itemRecord.variable_name || '').trim()
      if (!rawKey) continue
      if (!/^[a-zA-Z][a-zA-Z0-9_.]*$/.test(rawKey)) continue
      keys.push(rawKey)
    }
  }

  return uniqueList(keys)
}

export function resolveMentionContext(value: string, caretPosition: number): MentionContext | null {
  if (caretPosition < 0 || caretPosition > value.length) return null
  const prefix = value.slice(0, caretPosition)
  const openIndex = prefix.lastIndexOf('{{')
  if (openIndex === -1) return null
  const closedIndex = prefix.lastIndexOf('}}')
  if (closedIndex > openIndex) return null

  const query = prefix.slice(openIndex + 2)
  if (/[^a-zA-Z0-9_.]/.test(query)) return null
  return { start: openIndex, end: caretPosition, query }
}

export function buildSelectedToolDynamicKeys(selectedTools: MCPAction[]): Record<string, string[]> {
  const byName: Record<string, string[]> = {}
  selectedTools.forEach((tool) => {
    byName[tool.name.toLowerCase()] = extractDynamicVariableKeysFromTool(tool)
  })
  return byName
}

export function findDuplicateSelectedToolDynamicKeys(selectedTools: MCPAction[]): string[] {
  const counts: Record<string, number> = {}
  selectedTools.forEach((tool) => {
    extractDynamicVariableKeysFromTool(tool).forEach((key) => {
      counts[key] = (counts[key] || 0) + 1
    })
  })
  return Object.entries(counts)
    .filter(([, count]) => count > 1)
    .map(([key]) => key)
    .sort()
}

export function findUnknownReferencedDynamicTokens(
  text: string,
  selectedToolDynamicKeys: Record<string, string[]>,
  duplicateToolDynamicKeys: string[] = []
): string[] {
  const referencedTokens = extractMentionedDynamicTokens(text)
  const duplicateKeys = new Set(duplicateToolDynamicKeys)
  const unknown: string[] = []

  for (const token of referencedTokens) {
    const canonical = SYSTEM_ALIAS_TO_CANONICAL[token] || token
    if (canonical.startsWith('system.')) {
      if (!KNOWN_SYSTEM_TOKENS.has(canonical)) unknown.push(`{{${token}}}`)
      continue
    }
    if (canonical.startsWith('tool.')) {
      const key = canonical.slice('tool.'.length)
      if (duplicateKeys.has(key)) continue
      const existsInAnyTool = Object.values(selectedToolDynamicKeys).some((keys) => keys.includes(key))
      if (!existsInAnyTool) unknown.push(`{{${token}}}`)
      continue
    }
    unknown.push(`{{${token}}}`)
  }

  return uniqueList(unknown)
}

export function buildSystemMentionSuggestions(
  describeAlias: (token: string) => string
): MentionSuggestion[] {
  return SYSTEM_MENTION_SUGGESTIONS.map((suggestion) => ({
    token: suggestion.canonical,
    label: suggestion.label,
    category: 'variable',
    description: suggestion.aliasOf ? describeAlias(suggestion.aliasOf) : suggestion.canonical,
  }))
}

export function buildToolMentionSuggestions(
  selectedTools: MCPAction[],
  selectedToolDynamicKeys: Record<string, string[]>
): MentionSuggestion[] {
  const suggestions: MentionSuggestion[] = []

  selectedTools.forEach((tool) => {
    const keys = selectedToolDynamicKeys[tool.name.toLowerCase()] || []
    keys.forEach((key) => {
      suggestions.push({
        token: `tool.${key}`,
        label: `${tool.display_name}: tool.${key}`,
        category: 'variable',
        description: tool.name,
      })
    })
  })

  return suggestions
}

export function buildToolAtMentionSuggestions(selectedTools: MCPAction[]): MentionSuggestion[] {
  return selectedTools.map((tool) => ({
    token: `@${tool.name}`,
    label: `🔧 ${tool.name}`,
    category: 'tool',
    description: tool.display_name,
    badgeEmoji: '🔧',
  }))
}

export function buildTagAtMentionSuggestions(tags: Tag[]): MentionSuggestion[] {
  return tags.map((tag) => ({
    token: `@tag:${tag.id}`,
    label: `🏷️ ${tag.name}`,
    category: 'tag',
    description: tag.description || tag.id,
    badgeEmoji: '🏷️',
    badgeColor: tag.color,
  }))
}

/**
 * El color de la pildora de un documento, por su tipo. Es la excepcion 2 del
 * estandar -el tinte por tipo de archivo, que sirve para reconocerlo de un
 * vistazo- y vive en un solo sitio, que es esta funcion.
 */
/* eslint-disable no-restricted-syntax -- tinte por tipo de archivo, excepcion 2 del estandar de diseno */
export function getDocumentMentionColor(documentType?: string): string {
  const type = String(documentType || '').trim().toLowerCase()
  if (type === 'pdf') return '#ef4444'
  if (type === 'csv' || type === 'xls' || type === 'xlsx') return '#22c55e'
  if (type === 'doc' || type === 'docx') return '#3b82f6'
  if (type === 'url') return '#06b6d4'
  if (type === 'txt' || type === 'md') return '#64748b'
  return '#64748b'
}
/* eslint-enable no-restricted-syntax */

export function buildDocumentAtMentionSuggestions(documents: AgentDocument[]): MentionSuggestion[] {
  return documents.map((document) => ({
    token: `@doc:${document.id}`,
    label: `📃 ${document.title}`,
    category: 'document',
    description: String(document.document_type || '').toUpperCase() || 'DOC',
    badgeEmoji: '📃',
    badgeColor: getDocumentMentionColor(document.document_type),
  }))
}
